import { useEffect, useState } from 'react';

import type { CallRecord } from '../../types';

interface VideoCallProps {
  call: CallRecord;
  onEnd: (callId: string) => void;
  onAvatarStart: (callId: string) => void;
  onAvatarStop: (callId: string) => void;
  onTakeover: (callId: string) => void;
}

const VideoCall = ({ call, onEnd, onAvatarStart, onAvatarStop, onTakeover }: VideoCallProps) => {
  const [avatarActive, setAvatarActive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(!call.isVideo);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 3000);
    return () => window.clearTimeout(timer);
  }, []);

  if (!call.roomUrl) {
    return null;
  }

  const handleEnd = () => {
    if (avatarActive) {
      void onAvatarStop(call.id);
    }
    onEnd(call.id);
  };

  const handleAvatarToggle = () => {
    if (avatarActive) {
      void onAvatarStop(call.id);
      setAvatarActive(false);
    } else {
      void onAvatarStart(call.id);
      setAvatarActive(true);
    }
  };

  return (
    <div className="video-call-overlay" role="dialog" aria-label="Video-oproep">
      <div className="video-call-header">
        <h3>
          {call.isVideo ? 'Video-oproep' : 'Audio-oproep'} · {call.from}
        </h3>
        <div className="video-call-controls">
          <button
            type="button"
            className={`video-call-control-btn ${muted ? 'active' : ''}`}
            onClick={() => setMuted((prev) => !prev)}
            aria-label={muted ? 'Microfoon aanzetten' : 'Microfoon dempen'}
            title={muted ? 'Microfoon aanzetten' : 'Microfoon dempen'}
          >
            {muted ? '🔇' : '🎤'}
          </button>
          {call.isVideo && (
            <button
              type="button"
              className={`video-call-control-btn ${videoOff ? 'active' : ''}`}
              onClick={() => setVideoOff((prev) => !prev)}
              aria-label={videoOff ? 'Camera aanzetten' : 'Camera uitzetten'}
              title={videoOff ? 'Camera aanzetten' : 'Camera uitzetten'}
            >
              {videoOff ? '📷' : '📹'}
            </button>
          )}
          <button
            type="button"
            className="video-call-control-btn end"
            onClick={handleEnd}
            aria-label="Oproep beëindigen"
            title="Oproep beëindigen"
          >
            📞
          </button>
        </div>
      </div>
      <div className="video-call-body">
        {loading && (
          <div className="video-call-loading">
            <span className="recording-dot" style={{ width: 12, height: 12 }} />
            <p>Verbinding maken met videokamer…</p>
          </div>
        )}
        <iframe
          src={call.roomUrl}
          className="video-call-iframe"
          allow="camera; microphone; display-capture; autoplay; clipboard-write"
          title="Video-oproep kamer"
          onLoad={() => setLoading(false)}
        />
        <div className="video-call-avatar-panel">
          <button
            type="button"
            className={`video-call-avatar-btn ${avatarActive ? 'active' : ''}`}
            onClick={handleAvatarToggle}
          >
            {avatarActive ? '⏸ AI Avatar actief' : '▶ AI Avatar starten'}
          </button>
          {avatarActive && (
            <button
              type="button"
              className="video-call-avatar-btn danger"
              onClick={() => {
                void onTakeover(call.id);
                setAvatarActive(false);
              }}
            >
              ✋ Overnemen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCall;
